import { CSSProperties, useEffect, useRef, useState } from 'react';
import { first } from 'rxjs/operators';
import { CopyrightComponent } from '@vivli/shared/components';
import { Styles } from '@vivli/shared/theme';
import { AcceptedOrgInviteComponent } from './accepted-org-invite.component';
import { ButtonView, LoadIndicatorCenteredView } from '@vivli/shared/views';
import { useAuthEventsContext, useAuthServiceContext, useOrganizationsApiContext } from '@vivli/core/infrastructure/context';

const center500Style: CSSProperties = {
    textAlign: 'center',
    width: 500
};

interface AcceptOrgInviteModalProps {
    orgId: string;
}

export const AcceptOrgInviteModal = ({ orgId }: AcceptOrgInviteModalProps) => {
    const orgsService = useOrganizationsApiContext();
    const authService = useAuthServiceContext();
    const authEvents = useAuthEventsContext();

    const [accepted, setAccepted] = useState(false);
    const [isAcceptingOrg, setIsAcceptingOrg] = useState(false);
    const [error, setError] = useState(null);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [acceptedInvitation, setAcceptedInvitation] = useState(null);
    // Guard against re-entry: a successful sign-up now emits both 'signup-success' and (after the
    // follow-up silent login) 'get-user-info-success', so this handler can fire twice. A ref is used
    // because the auth subscription closes over first-render state and would not see setIsAcceptingOrg.
    const acceptingOrgRef = useRef(false);

    const handleOrgInvite = () => {
        if (acceptingOrgRef.current) {
            return;
        }
        acceptingOrgRef.current = true;
        setIsAcceptingOrg(true);
        orgsService
            .acceptOrganizationInvite(orgId)
            .pipe(first())
            .subscribe({
                next: (acceptedInvite) => {
                    setAcceptedInvitation(acceptedInvite);
                    setAccepted(true);
                    setIsAcceptingOrg(false);
                },
                error: (error) => {
                    acceptingOrgRef.current = false;
                    setAccepted(false);
                    setError(error);
                    setIsAcceptingOrg(false);
                }
            });
    };

    useEffect(() => {
        orgsService
            .getOrganization(orgId)
            .pipe(first())
            .subscribe((org) => {
                setCurrentOrg(org);
            });
    }, [orgId]);

    useEffect(() => {
        const authSub = authEvents.subscribe((event) => {
            switch (event) {
                case 'signup-success':
                case 'get-user-info-success':
                    handleOrgInvite();
                    break;
            }
        });

        return () => {
            authSub.unsubscribe();
        };
    }, []);

    if (isAcceptingOrg) {
        return <LoadIndicatorCenteredView />;
    } else if (accepted) {
        return <AcceptedOrgInviteComponent acceptedInvitation={acceptedInvitation} />;
    }

    return (
        <div style={center500Style}>
            Congratulations, you're almost done with accepting the invitation.
            <br />
            It looks like you haven't logged in yet, you'll need to do that now.
            <br />
            <br />
            Be sure to sign up with the email address <strong>{decodeURIComponent(location.search.split('=')[1])}</strong> or your
            acceptance of this invitation will not work.
            <br />
            <br />
            <br />
            <ButtonView style={{ ...Styles.Button.BUTTON_VIVLI_BLUE }} onClick={() => authService.signUp()}>
                Create Account
            </ButtonView>
            <br />
            <br />
            <ButtonView style={{ ...Styles.Button.BUTTON_LIGHT_VIVLI_BLUE }} onClick={() => authService.login()}>
                Existing Account
            </ButtonView>
            {error && (
                <div>
                    <div style={{ color: 'red' }}>
                        <br />
                        Error: {error}
                    </div>
                </div>
            )}
            <CopyrightComponent />
        </div>
    );
};
